import { useNavigate } from 'react-router-dom';
import React, { useRef, useState } from 'react';

import { jsPDF } from 'jspdf';
import { Honeybadger } from '@honeybadger-io/react';

import { requestApi } from 'api/Api';

import useTimezone from 'lib/hooks/useTimezone';
import { formatDate } from 'lib/helpers/dateUtils';
import { getFilestackClient } from 'lib/clients/filestack/filestackClient';

import Button from 'components/shared/Button/Button';
import Loading from 'components/shared/Loading/Loading';
import SignatureModal from 'components/shared/Modal/SignatureModal/SignatureModal';

const PAGE_CHARACTERS = 2300;
const LINE_WIDTH = 180;
const LINE_HEIGHT = 1.5;
const SIG_Y = 140;
const SIG_X = 10;
const SIG_WIDTH = 120;

export default function TechnologyConsentForm({ hideTechnologyModal, updateSurcharge, practice }) {
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signature, setSignature] = useState(null);
  const fullFormRef = useRef();
  const timezone = useTimezone();
  const today = formatDate(new Date(), timezone, 'MM/dd/yyyy');
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);

  const savePDF = async (handle) => {
    setLoader(true);
    try {
      await requestApi({
        url: '/api/practice/update',
        params: { fields: { technology_fee_consent: handle } },
        navigate
      });
      updateSurcharge();
      hideTechnologyModal();
    } catch (error) {
      Honeybadger.notify(
        `practice: ${practice.id}, file: TechnologyConsentForm, method: savePDF, error: ${error}`
      );
      console.error(error);
      throw error;
    } finally {
      setLoader(false);
    }
  };

  const printDocument = async (signatureSrc) => {
    if (!fullFormRef?.current || !signatureSrc) return;

    const rawText = fullFormRef.current.innerHTML;

    setLoader(true);
    try {
      const filestackClient = await getFilestackClient(navigate);
      if (!filestackClient) throw new Error('Filestack client unavailable');

      const text = rawText
        .replace(/&amp;/g, '&')
        .replace(/<[^>/]*>/g, '')
        .replace(/<[^>]*>/g, '\n')
        .replace(/\n\n[\n]*/g, '\n\n');
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', lineHeight: LINE_HEIGHT });
      const pages = text.length / PAGE_CHARACTERS;
      pdf.setFontSize(12);
      const words = text.split(' ');
      const textPages = [];
      let buffer = '';
      for (let i = 0; i < words.length; i++) {
        if (`${buffer} ${words[i]}`.length > PAGE_CHARACTERS) {
          textPages.push(buffer);
          buffer = words[i];
        } else {
          buffer += ` ${words[i]}`;
        }
      }
      if (buffer.length > 0) textPages.push(buffer);

      pdf.text(pdf.splitTextToSize(textPages[0], LINE_WIDTH, LINE_HEIGHT), 10, 10);
      for (let i = 1; i < pages; i++) {
        pdf.addPage();
        pdf.text(pdf.splitTextToSize(textPages[i], LINE_WIDTH, LINE_HEIGHT), 10, 10);
      }
      pdf.addImage(signatureSrc, 'PNG', SIG_X, SIG_Y, SIG_WIDTH, 0);
      const completePdf = pdf.output('blob');

      const result = await filestackClient.upload(completePdf);
      if (!result?.handle) throw new Error('Filestack upload did not return a handle');

      await savePDF(result.handle);
    } catch (error) {
      Honeybadger.notify(
        `practice: ${practice.id}, file: TechnologyConsentForm, method: printDocument, error: ${error}`
      );
      console.error(error);
      setLoader(false);
      throw error;
    }
  };

  const handleSign = async (signatureSrc) => {
    setSignature(signatureSrc);
    await printDocument(signatureSrc);
  };

  return loader ? (
    <Loading />
  ) : (
    <div className="border-slate-200 mx-auto w-full max-w-[640px] rounded-2xl border bg-white p-8 shadow-xl">
      <div
        ref={fullFormRef}
        className="text-slate-700 max-h-[60vh] space-y-8 overflow-y-auto pr-2 text-sm leading-relaxed">
        <div className="border-slate-100 border-b pb-4">
          <h3 className="text-slate-900 text-center text-lg font-600">
            Myriad Health LLC Miscellaneous Fee Calculator Compliance Agreement
          </h3>
        </div>
        <div>
          <p>
            Client has requested the ”Myriad Health LLC Miscellaneous Fee Calculator” add on
            feature. Client agrees to use the Miscellaneous Fee Calculator in compliance with any
            and all applicable federal, state, and local laws. Use of the Miscellaneous Fee
            Calculator in violation of any federal, state, or local laws, applicable health
            insurance company policies, or Payment Network rules may result in fines, penalties,
            non-payment or transaction chargebacks. Client understands that Myriad Health LLC is not
            responsible for any such violations of law, health insurance company policies, or
            Payment Network rules with respect to Client's use of Miscellaneous Fee Calculator,
            which may be switched on or off at the Client's discretion. Client understands that the
            Miscellaneous Fee Calculator can be adjusted or turned off at Client's discretion.
            Client understands and agrees that this fee is applied as a cost of doing business to{' '}
            ALL transaction types offered; client will not apply this fee to only credit/debit
            transactions.{' '}
          </p>
        </div>
        <div>
          <div className="border-slate-300 bg-slate-50 rounded-xl border border-dashed px-6 py-4">
            <p className="text-slate-800 flex flex-col gap-1 text-sm">
              <em className="font-bold">Client Business name: {practice.name}</em>
              <em className="font-bold">Date: {today}</em>
              <em className="font-bold">Signature:</em>
            </p>
          </div>
        </div>
      </div>
      <div className="border-slate-100 mt-8 border-t pt-6">
        <div className="flex items-center justify-between gap-6">
          {signature ? (
            <div className="flex flex-1 flex-col">
              <span className="text-slate-500 mb-2 text-xs font-semibold uppercase tracking-wide">
                Authorized signatory
              </span>
              <div className="border-slate-200 bg-slate-50 rounded-lg border px-4 py-3">
                <img src={signature} className="max-h-24 object-contain" />
              </div>
            </div>
          ) : (
            <div className="flex w-full justify-end">
              <Button text="Click to sign" onClick={() => setShowSignatureModal(true)} />
            </div>
          )}
        </div>
      </div>
      <SignatureModal
        showSignatureModal={showSignatureModal}
        hideSignatureModal={() => setShowSignatureModal(false)}
        onSign={handleSign}
      />
    </div>
  );
}
